// Local persistence, on localStorage.
//
// Offline is the primary operating condition, not a degraded one: Legend Valley
// with a full crowd has no usable cellular data, so everything the application
// reads on site is read from here and the sync layer is the only thing that ever
// touches the network.
//
// There are no schema versions and no upgrades. A store is one key holding a map
// of rows, reads and writes are synchronous, and a second tab of the site cannot
// hold anything open, which is the whole reason this is not IndexedDB. Photo
// bytes are the exception and live in their own database in localPhotos.ts,
// because blobs do not belong in a string store.

export interface OutboxEntry {
  id?: number;
  kind: string;
  payload: unknown;
  queuedAt: string;
}

export type DataStore =
  | 'festivals'
  | 'days'
  | 'stages'
  | 'walk'
  | 'artists'
  | 'sets'
  | 'ratings'
  | 'notes'
  | 'mustSee'
  | 'picks'
  | 'constraints'
  | 'plans'
  | 'profiles'
  | 'workspaces'
  | 'members'
  | 'votes'
  | 'ledger';

/** The fields that identify a row, in the order they compose its key. */
const KEY_PATHS: Record<DataStore, string[]> = {
  festivals: ['id'],
  days: ['id'],
  stages: ['id'],
  walk: ['festivalId'],
  artists: ['id'],
  sets: ['id'],
  ratings: ['userId', 'artistId'],
  notes: ['userId', 'artistId'],
  mustSee: ['userId', 'artistId'],
  picks: ['userId', 'setId'],
  constraints: ['id'],
  plans: ['id'],
  profiles: ['userId'],
  workspaces: ['id'],
  members: ['workspaceId', 'userId'],
  votes: ['conflictId', 'userId'],
  ledger: ['workspaceId', 'userId'],
};

export const STORAGE_PREFIX = 'festival-scheduler/';

const SEPARATOR = '::';

// A browser that refuses storage still runs the application for the session.
// Everything written goes here as well, and here only when localStorage throws.
const memory = new Map<string, string>();

function readRaw(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    if (value !== null) return value;
  } catch {
    // Private windows and blocked site data both throw rather than return null.
  }
  return memory.get(key) ?? null;
}

function writeRaw(key: string, value: string): void {
  memory.set(key, value);
  try {
    localStorage.setItem(key, value);
  } catch {
    // The in-memory copy above is the whole fallback.
  }
}

function removeRaw(key: string): void {
  memory.delete(key);
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing to do about a browser that will not forget.
  }
}

export function createStorage(prefix = STORAGE_PREFIX) {
  function readMap<T>(name: string): Record<string, T> {
    const raw = readRaw(prefix + name);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, T>) : {};
    } catch {
      // Unreadable content is treated as absent rather than as a reason to stop.
      return {};
    }
  }

  function writeMap<T>(name: string, rows: Record<string, T>): void {
    writeRaw(prefix + name, JSON.stringify(rows));
    if (['picks', 'ratings', 'notes', 'mustSee', 'profiles', 'constraints', 'workspaces', 'members', 'votes', 'ledger'].includes(name)) {
      const meta = readMap<unknown>('meta');
      meta.planRevision = Number(meta.planRevision ?? 0) + 1;
      meta.planTouched = true;
      writeRaw(prefix + 'meta', JSON.stringify(meta));
    }
  }

  function planRevision(): number { return Number(readMap<unknown>('meta').planRevision ?? 0); }

  /** Joins the identifying fields of a row into the string that indexes it. */
  function keyOf<T>(store: DataStore, row: T): string {
    const record = row as Record<string, unknown>;
    return KEY_PATHS[store].map((field) => String(record[field])).join(SEPARATOR);
  }

  /** Accepts the scalar or array form a caller passes for a row's key. */
  function keyFrom(key: unknown): string {
    return Array.isArray(key) ? key.map(String).join(SEPARATOR) : String(key);
  }

  /** Replaces every row in a store with the supplied rows. */
  async function replaceAll<T>(store: DataStore, rows: T[]): Promise<void> {
    const next: Record<string, T> = {};
    for (const row of rows) next[keyOf(store, row)] = row;
    writeMap(store, next);
  }

  /** Writes rows without removing what is already stored. */
  async function putAll<T>(store: DataStore, rows: T[]): Promise<void> {
    if (rows.length === 0) return;
    const next = readMap<T>(store);
    for (const row of rows) next[keyOf(store, row)] = row;
    writeMap(store, next);
  }

  async function getAll<T>(store: DataStore): Promise<T[]> {
    return Object.values(readMap<T>(store));
  }

  async function putOne<T>(store: DataStore, row: T): Promise<void> {
    const next = readMap<T>(store);
    next[keyOf(store, row)] = row;
    writeMap(store, next);
  }

  async function deleteOne(store: DataStore, key: unknown): Promise<void> {
    const next = readMap<unknown>(store);
    delete next[keyFrom(key)];
    writeMap(store, next);
  }

  async function meta<T>(key: string): Promise<T | undefined> {
    return readMap<T>('meta')[key];
  }

  async function setMeta(key: string, value: unknown): Promise<void> {
    const next = readMap<unknown>('meta');
    next[key] = value;
    if (key === 'configOverrides') { next.planTouched = true; next.planRevision = planRevision() + 1; }
    writeMap('meta', next);
  }

  /** Queues a write for the next time the network is reachable. */
  async function queueWrite(kind: string, payload: unknown): Promise<void> {
    const next = readMap<OutboxEntry>('outbox');
    const highest = Object.values(next).reduce((top, entry) => Math.max(top, entry.id ?? 0), 0);
    const id = highest + 1;
    next[String(id)] = { id, kind, payload, queuedAt: new Date().toISOString() };
    writeMap('outbox', next);
  }

  async function readOutbox(): Promise<OutboxEntry[]> {
    return Object.values(readMap<OutboxEntry>('outbox')).sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  }

  async function clearOutbox(ids: number[]): Promise<void> {
    const next = readMap<OutboxEntry>('outbox');
    for (const id of ids) delete next[String(id)];
    writeMap('outbox', next);
  }

  /** Says when the festival data was last cached, which the offline screen reports. */
  async function cachedAt(): Promise<string | undefined> {
    return meta<string>('cachedAt');
  }

  /** Empties every store. Nothing can hold this open, so it always goes through. */
  async function destroy(): Promise<boolean> {
    await wipe();
    return true;
  }

  async function wipe(): Promise<void> {
    const names: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);
        if (key?.startsWith(prefix) && !key.slice(prefix.length).includes('/') && key !== STORAGE_PREFIX + 'legacy-plan-owner') names.push(key);
      }
    } catch {
      // An unreadable localStorage still has the in-memory copy to clear.
    }
    for (const key of names) removeRaw(key);
    for (const key of [...memory.keys()]) {
      if (key.startsWith(prefix) && !key.slice(prefix.length).includes('/') && key !== STORAGE_PREFIX + 'legacy-plan-owner') removeRaw(key);
    }
  }

  return { replaceAll, putAll, getAll, putOne, deleteOne, meta, setMeta, planRevision,
    queueWrite, readOutbox, clearOutbox, cachedAt, destroy, wipe };
}

export type LocalStore = ReturnType<typeof createStorage>;
export const { replaceAll, putAll, getAll, putOne, deleteOne, meta, setMeta,
  queueWrite, readOutbox, clearOutbox, cachedAt, destroy, wipe } = createStorage();

export function accountStorage(userId: string | null): LocalStore {
  if (!userId) return createStorage();
  const prefix = `${STORAGE_PREFIX}accounts/${encodeURIComponent(userId)}/`;
  const storage = createStorage(prefix);
  if (readRaw(prefix + 'meta') === null) {
    const hasExisting = Object.keys(KEY_PATHS).some((name) => readRaw(prefix + name) !== null);
    void storage.setMeta('planOrigin', hasExisting ? 'unknown' : 'pristine');
  }
  // Preserve the pre-account device plan once. Later accounts start separately.
  const ownerKey = STORAGE_PREFIX + 'legacy-plan-owner';
  if (!readRaw(ownerKey)) {
    writeRaw(ownerKey, userId);
    let hasPlan = false;
    for (const name of [...Object.keys(KEY_PATHS), 'meta']) {
      const value = readRaw(STORAGE_PREFIX + name);
      if (value !== null && (name === 'meta' || readRaw(prefix + name) === null)) writeRaw(prefix + name, value);
      if (value && ['picks', 'ratings', 'notes', 'mustSee', 'profiles', 'constraints', 'members', 'workspaces'].includes(name)) {
        try { hasPlan ||= Object.keys(JSON.parse(value)).length > 0; } catch { /* Unreadable rows remain preserved. */ }
      }
    }
    if (hasPlan) void storage.setMeta('needsDevicePlanReview', true);
    const guestMeta = readRaw(STORAGE_PREFIX + 'meta');
    const history = ['picks', 'ratings', 'notes', 'mustSee', 'profiles', 'constraints', 'members', 'workspaces', 'votes', 'ledger']
      .some((name) => readRaw(STORAGE_PREFIX + name) !== null);
    let touched = false;
    try { const meta = JSON.parse(guestMeta ?? '{}'); touched = Boolean(meta.planTouched) || meta.configOverrides !== undefined; } catch { touched = true; }
    void storage.setMeta('planOrigin', history || touched ? 'unknown' : 'pristine');
  }
  return storage;
}

export function lastAccountId(): string | null {
  return readRaw(STORAGE_PREFIX + 'last-account-id');
}

export function rememberAccountId(id: string | null): void {
  if (id) writeRaw(STORAGE_PREFIX + 'last-account-id', id);
  else removeRaw(STORAGE_PREFIX + 'last-account-id');
}

// Called only after successful registration from the guest session, before mounting its store.
export function associateGuestPlan(userId: string): void {
  const prefix = `${STORAGE_PREFIX}accounts/${encodeURIComponent(userId)}/`;
  for (const name of [...Object.keys(KEY_PATHS), 'meta']) {
    const value = readRaw(STORAGE_PREFIX + name);
    if (value !== null) {
      if (name === 'meta') {
        let metadata: Record<string, unknown>;
        try { metadata = JSON.parse(value); if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) metadata = { unreadableGuestMetadata: value }; }
        catch { metadata = { unreadableGuestMetadata: value }; }
        const clean = Object.fromEntries(Object.entries(metadata).filter(([key]) => !key.startsWith('cloudBaseline:') && !key.startsWith('cloudObserved:')));
        clean.beforeGuestAssociation = metadata;
        writeRaw(prefix + name, JSON.stringify(clean));
      } else writeRaw(prefix + name, value);
    }
  }
  const store = createStorage(prefix);
  void store.setMeta('needsDevicePlanReview', false);
  void store.setMeta('planOrigin', 'signup');
  writeRaw(STORAGE_PREFIX + 'legacy-plan-owner', userId);
}
